import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  useIonToast,
  useIonViewWillEnter
} from "@ionic/react";
import {checkmarkDoneOutline} from "ionicons/icons";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import {MenuButton} from "app/employee/menu/MenuButton";
import {Trans, useTranslation} from "react-i18next";
import type {NotificationFragment} from "app/gql/graphqlSchema";
import {LoadingLoadable} from "app/employee/Loading";
import {type ComponentProps, type ReactNode, useCallback} from "react";
import {isNil} from "app/utils/stdlib";
import {css, styled} from "styled-components";
import {Timestamp} from "app/employee/Timestamp";
import {DateTime} from "luxon";
import {DateDisplay} from "app/employee/DateDisplay";
import {linkEmplBroadcast, linkEmplOrganizer, linkEmplTicketDetail} from "app/employee/core/employeeLinks";
import {parseAnyDate} from "app/utils/dates";
import {
  markAllAsReadInAppNotificationsAndReload,
  markAsReadInAppNotificationAndReload
} from "app/employee/notification/utils";
import type {Updater} from "use-immer";
import {TitleRow} from "app/employee/incdoc/TitleRow";
import {useNotifications} from "app/employee/notification/useNotifications";
import {useNotificationCount} from "app/employee/notification/useNotificationCount";
import {NativeNotificationSettingsFab} from "app/employee/nativeApp/NativeNotificationSettingsFab";

const DisplayNotificationContent = ({notification}: {notification: NotificationFragment}) => {
  return <>{notification.content.text}</>;
};

const unreadCss = css`
  font-weight: bold;
`;

const NotificationIonItem = styled(IonItem)<{$unread?: boolean}>`
  ${({$unread}) => $unread && unreadCss}
`;

const ReadAt = styled.p`
  && {
    margin-top: 0.5rem;
  }
`;

type NotificationIonItemProps = ComponentProps<typeof NotificationIonItem>;

const NotificationItem = ({
  notification,
  setNotifications
}: {
  notification: NotificationFragment;
  setNotifications: Updater<NotificationFragment[]>;
}) => {
  const {t} = useTranslation();
  const isUnread = isNil(notification.readAt);
  const handleMarkAsRead = useCallback(async () => {
    if (isUnread) {
      const notifications = await markAsReadInAppNotificationAndReload(notification.deliveryId);
      setNotifications(notifications);
    }
  }, [isUnread, notification.deliveryId, setNotifications]);

  const itemProps: NotificationIonItemProps = {
    $unread: isUnread
  };

  switch (notification.__typename) {
    case "ManagerBroadcastInAppNotification":
      itemProps.detail = true;
      itemProps.routerLink = linkEmplBroadcast(notification.managerBroadcastId.broadcastId, notification.deliveryId);
      break;
    case "TextInAppNotification":
      itemProps.onClick = handleMarkAsRead;
      break;
    case "OrgDocInAppNotification":
      itemProps.routerLink = linkEmplOrganizer(notification.orgDoc.location.id.locationId);
      itemProps.routerDirection = "root";
      itemProps.onClick = handleMarkAsRead;
      break;
    case "TicketAssignedInAppNotification":
    case "TicketStatusChangedInAppNotification":
      itemProps.routerLink = linkEmplTicketDetail(notification.ticket.id.ticketId);
      itemProps.routerDirection = "root";
      itemProps.onClick = handleMarkAsRead;
      break;
  }
  return (
    <NotificationIonItem {...itemProps}>
      <IonLabel>
        <DisplayNotificationContent notification={notification} />
        {!isUnread && (
          <ReadAt>
            <Trans
              components={{
                ReadAtTime: <Timestamp ts={notification.readAt} />
              }}
              i18nKey="notifications.read-at"
              t={t}
            />
          </ReadAt>
        )}
      </IonLabel>
    </NotificationIonItem>
  );
};

export const Notifications = () => {
  const {t} = useTranslation();
  const {data: notifications, fetch, refetch, setData} = useNotifications();
  const {unreadCount} = useNotificationCount();
  const [presentToast] = useIonToast();

  useIonViewWillEnter(() => {
    fetch({});
  }, [fetch]);

  const handleMarkAll = useCallback(async () => {
    const updated = await markAllAsReadInAppNotificationsAndReload();
    setData(updated);
    await presentToast({
      message: t("notifications.marked-all-as-read"),
      duration: 1500,
      position: "top",
      color: "success"
    });
  }, [presentToast, setData, t]);

  let lastSentAt: DateTime | null = null;

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>{t("notifications.title", {count: unreadCount ?? 0})}</IonTitle>
          <IonButtons slot="end">
            <IonButton color="primary" onClick={handleMarkAll}>
              <IonIcon icon={checkmarkDoneOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
          <MenuButton />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <LoadingLoadable loadable={notifications} />
        <IonRefresher onIonRefresh={refetch} slot="fixed">
          <IonRefresherContent />
        </IonRefresher>

        <NativeNotificationSettingsFab />

        {!isNil(notifications.value) && notifications.value.length === 0 && (
          <TitleRow className="ion-padding-horizontal">
            <h1>{t("notifications.no-notifications")}</h1>
          </TitleRow>
        )}
        <IonList>
          {notifications.value?.map((n) => {
            let dateComponent: ReactNode | null = null;
            const sentAtLuxon = parseAnyDate(n.sentAt);
            if (isNil(lastSentAt) || !sentAtLuxon.hasSame(lastSentAt, "day")) {
              dateComponent = (
                <IonListHeader key={n.sentAt}>
                  <IonLabel>
                    <DateDisplay dt={sentAtLuxon} />
                  </IonLabel>
                </IonListHeader>
              );
            }
            lastSentAt = sentAtLuxon;

            return [dateComponent, <NotificationItem key={n.deliveryId} notification={n} setNotifications={setData} />];
          })}
        </IonList>
      </IonContent>
    </>
  );
};
